import { plateFont, numFont } from '../../../utils/fonts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LinkIcon from '@mui/icons-material/Link';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import SendIcon from '@mui/icons-material/Send';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserStore } from 'store';
import { can } from '../../../utils/access';
import { usePaginatedList } from '../../../hooks/usePaginatedList';
import DataTable from '../../shared/DataTable';
import PaginationControls from '../../shared/PaginationControls';
import { FilterChip, SearchInput } from '../../shared/ListFilters';
import { formatPrice } from '../../../utils/vat';
import { formatDate } from '../../../utils';
import InboxAddressCard from './InboxAddressCard';
import { NoteStatusChip } from './InboundStatusChip';
import InboundDeliveryNoteDetailDialog from './InboundDeliveryNoteDetailDialog';
import UploadLaheteDialog from './UploadLaheteDialog';

// Scopes shown as chips above the list. Handled lähetteet are part of the record
// (nothing is ever deleted), so reaching them must not depend on opening a filter
// panel — "did I already handle the Meko lähete?" is a one-click question.
// "Tilauksilla": lähetteet identified by an explicit order reference and parked on
// that order — handled from the Tilatut row, so they are out of "Avoimet" and the
// badges but never out of reach.
const STATUS_SCOPES = [
  { id: 'open', value: 'open', labelKey: 'inboundDeliveries.filter.open', countKey: 'open' },
  { id: 'awaiting_order', value: 'awaiting_order', labelKey: 'inboundDeliveries.filterAwaitingOrder', countKey: 'awaiting_order' },
  { id: 'allocated', value: 'allocated', labelKey: 'inboundDeliveries.filter.allocated', countKey: 'allocated' },
  { id: 'dismissed', value: 'dismissed', labelKey: 'inboundDeliveries.filter.dismissed', countKey: 'dismissed' },
  { id: 'all', value: '', labelKey: 'inboundDeliveries.filter.all', countKey: 'total' },
];

const EMPTY_COUNTS = {
  new: 0,
  failed: 0,
  open: 0,
  awaiting_order: 0,
  orders_with_lahete: 0,
  allocated: 0,
  dismissed: 0,
  total: 0,
};

const AUTO_HINT_DISMISSED_KEY = 'inboundAutoResolveHintDismissed';

// "Saapuneet lähetteet" tab of the supplier orders view: inbound delivery notes
// received by email (per-org inbox address) or manual upload.
function InboundDeliveryNotesTab({ t, onCountsChange }) {
  const [statusFilter, setStatusFilter] = useState('open');
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [countsLoaded, setCountsLoaded] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  // Nudge towards the automatic mode: only for someone who can turn it on, only
  // while it is off, and only until they close it on this browser.
  const [autoHintDismissed, setAutoHintDismissed] = useState(() => {
    try {
      return localStorage.getItem(AUTO_HINT_DISMISSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const showAutoHint =
    !autoHintDismissed &&
    !!currentOrganization &&
    !currentOrganization.inbound_delivery_notes_auto_resolve &&
    can(currentOrganization, 'settings.features');
  const dismissAutoHint = () => {
    setAutoHintDismissed(true);
    try {
      localStorage.setItem(AUTO_HINT_DISMISSED_KEY, '1');
    } catch (e) {
      // Private mode or blocked storage: the hint simply comes back next time.
    }
  };

  const serverFilters = useMemo(
    () => (statusFilter ? { status: statusFilter } : {}),
    [statusFilter]
  );

  const {
    data: notes,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    refetch,
    setFilters,
  } = usePaginatedList('/api/inbound-delivery-notes/', {
    initialFilters: serverFilters,
    defaultSortField: 'created_at',
    defaultSortDirection: 'desc',
    defaultPageSize: 50,
  });

  useEffect(() => {
    setFilters(serverFilters);
  }, [serverFilters, setFilters]);

  const fetchCounts = useCallback(async () => {
    try {
      const response = await axios.get('/api/inbound-delivery-notes/counts/');
      setCounts({ ...EMPTY_COUNTS, ...response.data });
      setCountsLoaded(true);
      onCountsChange?.(response.data);
    } catch {
      // Counts stay unloaded on purpose: the full list UI is the safe fallback,
      // never the activation empty state.
    }
  }, [onCountsChange]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Notes still being read: poll so parsed lines appear without a manual refresh.
  const hasProcessing = notes.some(
    (n) => n.processing_status === 'pending' || n.processing_status === 'processing'
  );
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (!hasProcessing) return;
    const interval = setInterval(() => {
      refetchRef.current();
      fetchCounts();
    }, 8000);
    return () => clearInterval(interval);
  }, [hasProcessing, fetchCounts]);

  // ?note=<id> makes a single lähete linkable: a handled one can be bookmarked or
  // pasted into a message instead of being hunted down in the list again.
  const syncNoteParam = useCallback(
    (noteId) => {
      const next = new URLSearchParams(searchParams);
      if (noteId) next.set('note', String(noteId));
      else next.delete('note');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const initialNoteParam = useRef(searchParams.get('note'));
  useEffect(() => {
    const id = parseInt(initialNoteParam.current, 10);
    if (!Number.isNaN(id)) {
      setSelectedNoteId(id);
      setDetailOpen(true);
    }
  }, []);

  const openNote = (noteId) => {
    setSelectedNoteId(noteId);
    setDetailOpen(true);
    syncNoteParam(noteId);
  };

  const closeNote = () => {
    setDetailOpen(false);
    setSelectedNoteId(null);
    syncNoteParam(null);
  };

  const handleOpen = (note) => openNote(note.id);

  const handleChanged = useCallback(() => {
    refetchRef.current();
    fetchCounts();
  }, [fetchCounts]);

  const handleUploaded = (note) => {
    setSnackbar({ open: true, message: t('inboundDeliveries.uploadSuccess'), severity: 'success' });
    handleChanged();
    openNote(note.id);
  };

  // The activation pitch belongs to an org that has NEVER received a lähete. Once
  // anything has ever arrived, the list stays on screen even when every note is
  // handled — otherwise the history becomes unreachable.
  const isEmpty = countsLoaded && !loading && !searchQuery && counts.total === 0;

  // A search that misses inside the current scope almost always means the note was
  // already handled; offer the wider scope instead of a dead end.
  const canWidenSearch = !loading && totalCount === 0 && !!searchQuery && statusFilter !== '';

  const renderMatchCell = (note) => {
    if (note.confirmed_order) {
      return (
        <Chip
          icon={<LinkIcon sx={{ fontSize: 14 }} />}
          label={note.confirmed_order.display_reference}
          size="small"
          sx={{ bgcolor: '#e6ffed', color: '#2f8c54', fontWeight: 600, fontSize: '12px' }}
        />
      );
    }
    if (note.suggested_order && note.order_match_kind === 'exact') {
      return (
        <Chip
          icon={<LinkIcon sx={{ fontSize: 14 }} />}
          label={t('inboundDeliveries.exactChip', {
            reference: note.suggested_order.display_reference,
          })}
          size="small"
          data-testid={`inbound-exact-chip-${note.id}`}
          sx={{ bgcolor: '#fef3e2', color: '#9c4221', fontWeight: 600, fontSize: '12px' }}
        />
      );
    }
    if (note.suggested_order) {
      return (
        <Chip
          icon={<LightbulbOutlinedIcon sx={{ fontSize: 14 }} />}
          label={t('inboundDeliveries.suggestionChip', {
            reference: note.suggested_order.display_reference,
          })}
          size="small"
          sx={{ bgcolor: '#ebf4ff', color: '#2c5282', fontWeight: 600, fontSize: '12px' }}
        />
      );
    }
    if (note.processing_status !== 'done') {
      return <Typography sx={{ color: 'text.disabled' }}>—</Typography>;
    }
    return (
      <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
        {t('inboundDeliveries.noMatch')}
      </Typography>
    );
  };

  if (isEmpty) {
    return (
      <>
        <EmptyState t={t} onUpload={() => setUploadOpen(true)} />
        <UploadLaheteDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onUploaded={handleUploaded}
        />
        <InboundDeliveryNoteDetailDialog
          open={detailOpen}
          noteId={selectedNoteId}
          onClose={closeNote}
          onChanged={handleChanged}
        />
      </>
    );
  }

  return (
    <Box data-testid="inbound-delivery-notes-tab">
      {/* Status scopes — handled lähetteet are one click away, not behind a panel */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {STATUS_SCOPES.map((scope) => (
          <FilterChip
            key={scope.id}
            label={`${t(scope.labelKey)} (${counts[scope.countKey] ?? 0})`}
            active={statusFilter === scope.value}
            color="primary"
            onClick={() => {
              setStatusFilter(scope.value);
              setPage(1);
            }}
            data-testid={`inbound-scope-${scope.id}`}
          />
        ))}
      </Box>

      {/* Search row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
          placeholder={t('inboundDeliveries.searchPlaceholder')}
          data-testid="inbound-search"
        />
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setUploadOpen(true)}
          sx={{ flexShrink: 0 }}
          data-testid="lahete-upload-button"
        >
          {t('inboundDeliveries.uploadButton')}
        </Button>
      </Box>

      {canWidenSearch && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              onClick={() => {
                setStatusFilter('');
                setPage(1);
              }}
              data-testid="inbound-search-all-statuses"
            >
              {t('inboundDeliveries.searchAllStatuses')}
            </Button>
          }
        >
          {t('inboundDeliveries.searchNoHitsInScope')}
        </Alert>
      )}

      {/* Inbox address */}
      <Box sx={{ mb: 2 }}>
        <InboxAddressCard compact />
      </Box>

      {showAutoHint && (
        <Alert
          severity="info"
          icon={<AutoModeIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
          onClose={dismissAutoHint}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size="small"
                color="inherit"
                onClick={() => navigate('/settings?tab=features&subtab=inbound_deliveries')}
                data-testid="inbound-auto-resolve-hint-link"
              >
                {t('Go to settings')}
              </Button>
              <Button size="small" color="inherit" onClick={dismissAutoHint} data-testid="inbound-auto-resolve-hint-close">
                {t('Close')}
              </Button>
            </Box>
          }
          data-testid="inbound-auto-resolve-hint"
        >
          {t('inboundDeliveries.autoResolveHint')}
        </Alert>
      )}

      {/* Failed-parse attention banner */}
      {counts.failed > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{t('inboundDeliveries.failedBannerTitle', { count: counts.failed })}</strong>{' '}
          {t('inboundDeliveries.failedBannerBody')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('Error')}: {error}
        </Alert>
      )}

      {/* Desktop table */}
      <DataTable
        columns={[
          {
            key: 'supplier',
            label: t('inboundDeliveries.columns.supplier'),
            maxWidth: 220,
            render: (note) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {note.source === 'upload' ? (
                  <UploadFileIcon sx={{ fontSize: 17, color: 'text.disabled', flexShrink: 0 }} />
                ) : (
                  <MailOutlineIcon sx={{ fontSize: 17, color: 'text.disabled', flexShrink: 0 }} />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }} noWrap>
                    {note.supplier_name || (
                      <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        {t('inboundDeliveries.unknownSupplier')}
                      </Box>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled' }} noWrap>
                    {note.source === 'upload' ? note.created_by_name : note.sender_email}
                  </Typography>
                </Box>
              </Box>
            ),
          },
          {
            key: 'lahete_number',
            label: t('inboundDeliveries.columns.number'),
            noWrap: true,
            render: (note) =>
              note.lahete_number ? (
                <Typography sx={{ ...numFont, fontSize: '13px' }}>
                  {note.lahete_number}
                </Typography>
              ) : (
                <Typography sx={{ color: 'text.disabled' }}>—</Typography>
              ),
          },
          {
            key: 'registrations',
            label: t('inboundDeliveries.columns.registrations'),
            noWrap: true,
            render: (note) =>
              (note.registration_numbers || []).length ? (
                <Box>
                  {note.registration_numbers.slice(0, 3).map((reg) => (
                    <Typography key={reg} sx={{ ...plateFont, fontSize: '13px' }}>
                      {reg}
                    </Typography>
                  ))}
                  {note.registration_numbers.length > 3 && (
                    <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                      +{note.registration_numbers.length - 3}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography sx={{ color: 'text.disabled' }}>—</Typography>
              ),
          },
          {
            key: 'match',
            label: t('inboundDeliveries.columns.match'),
            render: renderMatchCell,
          },
          {
            key: 'total',
            label: t('inboundDeliveries.columns.totalNet'),
            align: 'right',
            noWrap: true,
            render: (note) =>
              note.total_net != null ? (
                <Typography sx={{ fontSize: '14px', fontWeight: 600, ...numFont }}>
                  {formatPrice(note.total_net)} €
                </Typography>
              ) : (
                <Typography sx={{ color: 'text.disabled' }}>—</Typography>
              ),
          },
          {
            key: 'received',
            label: t('inboundDeliveries.columns.received'),
            noWrap: true,
            render: (note) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {formatDate(note.email_received_at || note.created_at)}
              </Typography>
            ),
          },
          {
            key: 'handled',
            label: t('inboundDeliveries.columns.handled'),
            noWrap: true,
            render: (note) =>
              note.handled_at ? (
                <Box data-testid={`inbound-note-handled-${note.id}`}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {formatDate(note.handled_at)}
                  </Typography>
                  {(note.handled_by_kind === 'auto' || note.handled_by_name) && (
                    <Typography sx={{ fontSize: '12px', color: 'text.disabled' }} noWrap>
                      {note.handled_by_kind === 'auto'
                        ? t('inboundDeliveries.handledAutomatically')
                        : note.handled_by_name}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography sx={{ color: 'text.disabled' }}>—</Typography>
              ),
          },
          {
            key: 'status',
            label: t('Status'),
            width: '1%',
            noWrap: true,
            render: (note) => <NoteStatusChip note={note} />,
          },
        ]}
        data={notes}
        loading={loading}
        onRowClick={handleOpen}
        testIdPrefix="inbound-note-row"
        emptyIcon={<MoveToInboxIcon />}
        emptyTitle={
          searchQuery || statusFilter !== 'open'
            ? t('inboundDeliveries.noNotesMatch')
            : t('inboundDeliveries.noNotes')
        }
        emptyDescription={t('inboundDeliveries.noNotesDescription')}
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile cards */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        {loading && notes.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            {notes.map((note) => (
              <Paper
                key={note.id}
                data-testid={`inbound-note-row-${note.id}`}
                elevation={0}
                onClick={() => handleOpen(note)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600 }} noWrap>
                        {note.supplier_name || t('inboundDeliveries.unknownSupplier')}
                      </Typography>
                      <NoteStatusChip note={note} />
                    </Box>
                    {note.lahete_number && (
                      <Typography sx={{ ...numFont, fontSize: '13px', color: 'text.secondary' }}>
                        {note.lahete_number}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                      {(note.registration_numbers || []).join(', ')}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    {note.total_net != null && (
                      <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                        {formatPrice(note.total_net)} €
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      {formatDate(note.email_received_at || note.created_at)}
                    </Typography>
                    {note.handled_at && (
                      <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                        {t('inboundDeliveries.columns.handled')}: {formatDate(note.handled_at)}
                        {note.handled_by_kind === 'auto'
                          ? ` · ${t('inboundDeliveries.handledAutomatically')}`
                          : note.handled_by_name
                            ? ` · ${note.handled_by_name}`
                            : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </>
        )}
      </Box>

      {/* Dialogs */}
      <UploadLaheteDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={handleUploaded}
      />
      <InboundDeliveryNoteDetailDialog
        open={detailOpen}
        noteId={selectedNoteId}
        onClose={closeNote}
        onChanged={handleChanged}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Activation-focused empty state: the inbox address + how it works.
function EmptyState({ t, onUpload }) {
  const steps = [
    { title: t('inboundDeliveries.emptyStep1Title'), body: t('inboundDeliveries.emptyStep1Body') },
    { title: t('inboundDeliveries.emptyStep2Title'), body: t('inboundDeliveries.emptyStep2Body') },
    { title: t('inboundDeliveries.emptyStep3Title'), body: t('inboundDeliveries.emptyStep3Body') },
  ];
  const icons = [
    <ContentCopyIcon key="i1" sx={{ fontSize: 18 }} />,
    <SendIcon key="i2" sx={{ fontSize: 18 }} />,
    <MoveToInboxIcon key="i3" sx={{ fontSize: 18 }} />,
  ];
  return (
    <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 } }} data-testid="inbound-empty-state">
      <Box sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center' }}>
        <Box
          sx={{
            width: 62,
            height: 62,
            borderRadius: '16px',
            bgcolor: '#ebf4ff',
            color: '#3182ce',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <MarkEmailUnreadIcon sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('inboundDeliveries.emptyTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3, lineHeight: 1.6 }}>
          {t('inboundDeliveries.emptyBody')}
        </Typography>
        <InboxAddressCard />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2.5,
            mt: 4,
            textAlign: 'left',
          }}
        >
          {steps.map((step, index) => (
            <Box key={step.title}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'background.default',
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {icons[index]}
                </Box>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700 }}>
                  {index + 1}. {step.title}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, color: 'text.secondary' }}>
                {step.body}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            justifyContent: 'center',
            mt: 4,
            pt: 3,
            borderTop: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('inboundDeliveries.emptyUploadHint')}
          </Typography>
          <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={onUpload}>
            {t('inboundDeliveries.uploadButton')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default withTranslation()(InboundDeliveryNotesTab);
